import * as atatus from 'atatus-spa';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { StepsModule } from 'primeng/steps';

import {
  CommonModule,
  LocationStrategy,
  PathLocationStrategy,
} from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  Injector,
  NgModule,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  appTranslationsInitializerFactory,
  AtatusErrorHandler,
  CkEditorPersonalizationPluginModule,
  CommonServicesModule,
  ERROR_LOGGER_PROVIDERS,
  FEATURE_FLAG_API,
  HttpModule,
  LCID_QUERYSTRING_PARAM_NAME,
  LOG_LOGGER_PROVIDERS,
  Logger,
  MonitoringModule,
  Utils,
  WEB_API_URL,
} from '@clickd-angular/common-lib';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';

import { FeatureflagServiceV2 } from '@clickd-angular/common-lib';
import { DropdownModule } from 'primeng/dropdown';
import { environment } from '../environments/environment';
import { version } from '../environments/version';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { httpInterceptorProviders } from './app.interceptors';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { HttpHelper } from './helpers/http-helpers';

if (environment.name !== 'LOCAL') {
  //we want atatus to run only on production environments includeing dev/qa/pre-prod
  atatus
    .config('074b38131a1748af9d90ec5f55c5f3de', {
      // Enable or Disable all console activity
      console: true,

      // By default Atatus will watch all console activity and include that information in the Timeline
      consoleTimeline: true,

      // Set this to false if you don't want your users seeing anything when the dev tools open.
      consoleDisplay: true,

      // Set this to false if you don't want console.error() to trigger errors.
      consoleErrorNotify: true,

      // Specify levels only if you want to watch certain console functions
      consoleTimelineLevels: ['log', 'debug', 'info', 'warn', 'error'],
    })
    .install()
    .setUser(Utils.getParameterByName('accountKey'))
    .setCustomData({
      userid: Utils.getParameterByName('userid'),
    });
}

// AoT requires an exported function for factories
export function multiHttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/i18n/common/', suffix: '.json' },
    { prefix: './assets/i18n/', suffix: '.json' },
  ]);
}

export function versionLoggerFactory(logger: Logger) {
  return () =>
    new Promise<any>((resolve: any) => {
      logger.log(version);
      resolve();
    });
}

export function atatusErrorHandlerFactory() {
  return new AtatusErrorHandler(
    Utils.getParameterByName('accountKey'),
    environment.name
  );
}

// Decide on the right logger config based on the type of environment
const LoggerProviderBasedOnEnvironment = environment.production
  ? ERROR_LOGGER_PROVIDERS
  : LOG_LOGGER_PROVIDERS;

@NgModule({
  declarations: [AppComponent, PageNotFoundComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    StepsModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    // common-lib modules
    MonitoringModule.forRoot(),
    CommonServicesModule.forRoot(),
    HttpClientModule,
    HttpModule,
    CkEditorPersonalizationPluginModule,
    // end of common-lib modules
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: multiHttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    LoggerProviderBasedOnEnvironment,
    { provide: LCID_QUERYSTRING_PARAM_NAME, useValue: 'userlcid' },
    {
      provide: APP_INITIALIZER,
      useFactory: appTranslationsInitializerFactory,
      deps: [
        TranslateService,
        Injector,
        TranslateLoader,
        LCID_QUERYSTRING_PARAM_NAME,
      ],
      multi: true,
    },
    {
      //this is needed so version will have some reference and will not be omitted by the compiler
      provide: APP_INITIALIZER,
      useFactory: versionLoggerFactory,
      deps: [Logger],
      multi: true,
    },
    { provide: ErrorHandler, useFactory: atatusErrorHandlerFactory },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: WEB_API_URL, useValue: environment.mscrmappServiceApiUrl },
    { provide: FEATURE_FLAG_API, useValue: environment.credentialsUrl },

    httpInterceptorProviders,
    HttpHelper,
    FeatureflagServiceV2,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
